import { Sparkles } from 'lucide-react'
import { useChatStore } from '@/stores/chat-store'
import { usePanelInsightsStore } from '@/stores/panel-insights-store'

/** Friendly panel names for the rail. */
const PANEL_LABELS: Record<string, string> = {
  'history-sankey': 'Flow Graph',
  'history-bar-category': 'By category',
  'history-bar': 'Bar',
  'history-time-series': 'Time series',
  'history-scatter': 'Scatter',
  'history-intraday': 'Intraday',
}

const FOLLOW_UPS = [
  'Split this by gap size',
  'What time does the move usually start?',
  'Show day-2 continuation',
]

function panelLabel(componentType: string): string {
  return PANEL_LABELS[componentType] ?? componentType.replace(/^history-/, '')
}

/**
 * Ask AI rail briefing — shown in the drawer's empty state on the History page.
 * Aggregates every panel's current insight ("what your panels are saying"),
 * surfaces the panel the user is following up on (seeded via the ✨ footer
 * button), and offers a few suggested follow-ups that fire a chat message.
 */
export function HistoryBriefing({ onAsk }: { onAsk: (text: string) => void }) {
  const insights = usePanelInsightsStore((s) => s.insights)
  const context = useChatStore((s) => s.context)
  const list = Object.values(insights).filter((ins) => ins.text)

  if (list.length === 0 && !context) return null

  return (
    <div className="flex flex-col gap-3 p-3">
      {context && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary/80">
            <Sparkles className="h-3 w-3 shrink-0" />
            Following up on {panelLabel(context.componentType)}
          </div>
          <p className="text-sm leading-snug text-foreground">{context.insight}</p>
        </div>
      )}

      {list.length > 0 && (
        <div className="rounded-lg border border-border/50 bg-card/50 p-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            What your panels are saying
          </div>
          <div className="flex flex-col gap-2">
            {list.map((ins) => (
              <p key={ins.panelId} className="text-xs leading-snug text-muted-foreground">
                <span className="font-semibold text-primary">{panelLabel(ins.componentType)}</span>{' '}
                — {ins.text}
              </p>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Suggested follow-ups
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FOLLOW_UPS.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onAsk(q)}
              className="rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-hover hover:text-foreground"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
